import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { getLivestreamsFeed, getMyLivestreams } from "../utils/livestreamApi";

// Enhanced fetch with pagination support
export const fetchLivestreams = createAsyncThunk(
    "livestreams/fetchLivestreams",
    async (params = {}, { rejectWithValue }) => {
        try {
            console.log('[Redux] fetchLivestreams - Calling getLivestreamsFeed API with params:', params);
            const response = await getLivestreamsFeed(params);
            console.log('[Redux] fetchLivestreams - Received response:', response);
            
            // Handle new PageDto structure: { items: T[], page: number, size: number, totalElements: number, totalPages: number, sort: string }
            let pageData;
            if (response?.items !== undefined) {
                // New PageDto structure
                pageData = response;
            } else if (response?.success !== undefined) {
                // ApiEnvelope with PageDto
                pageData = response.data;
            } else {
                // Legacy structure fallback
                const items = response?.data?.data || response?.data || response || [];
                pageData = {
                    items: Array.isArray(items) ? items : [],
                    page: 0,
                    size: items.length || 0,
                    totalElements: items.length || 0,
                    totalPages: 1,
                    sort: ""
                };
            }
            
            console.log('[Redux] fetchLivestreams - Processed page data:', pageData);
            console.log('[Redux] fetchLivestreams - Livestream objects details:', pageData.items?.map((ls, index) => ({
                index,
                id: ls.id,
                description: ls.description,
                status: ls.status,
                startTime: ls.startTime,
                endTime: ls.endTime,
                price: ls.price,
                viewersPrice: ls.viewersPrice,
                requiresPayment: ls.requiresPayment,
                freeLivestream: ls.freeLivestream,
                liveStreamPurchaseIds: ls.liveStreamPurchaseIds,
                courtId: ls.courtId,
                court: ls.court
            })));
            
            return pageData;
        } catch (error) {
            console.error('[Redux] fetchLivestreams - Error:', error);
            // Return a proper error message string
            const errorMessage = error.response?.data?.message || error.message || "Failed to load livestreams.";
            return rejectWithValue(errorMessage);
        }
    }
);

export const fetchMyLivestreams = createAsyncThunk(
    "livestreams/fetchMyLivestreams",
    async (params = {}, { rejectWithValue }) => {
        try {
            console.log('[Redux] fetchMyLivestreams - Calling getMyLivestreams API with params:', params);
            const response = await getMyLivestreams(params);
            console.log('[Redux] fetchMyLivestreams - Received response:', response);
            
            // Handle new PageDto structure
            let pageData;
            if (response?.items !== undefined) {
                // New PageDto structure
                pageData = response;
            } else if (response?.success !== undefined) {
                // ApiEnvelope with PageDto
                pageData = response.data;
            } else {
                // Legacy structure fallback
                const items = response?.data?.data || response?.data || response || [];
                pageData = {
                    items: Array.isArray(items) ? items : [],
                    page: 0,
                    size: items.length || 0,
                    totalElements: items.length || 0,
                    totalPages: 1,
                    sort: ""
                };
            }
            
            console.log('[Redux] fetchMyLivestreams - Processed page data:', pageData);
            console.log('[Redux] fetchMyLivestreams - Livestream objects details:', pageData.items?.map((ls, index) => ({
                index,
                id: ls.id,
                description: ls.description,
                status: ls.status,
                startTime: ls.startTime,
                endTime: ls.endTime,
                price: ls.price,
                viewersPrice: ls.viewersPrice,
                requiresPayment: ls.requiresPayment,
                freeLivestream: ls.freeLivestream,
                liveStreamPurchaseIds: ls.liveStreamPurchaseIds,
                courtId: ls.courtId,
                court: ls.court
            })));
            
            return pageData;
        } catch (error) {
            console.error('[Redux] fetchMyLivestreams - Error:', error);
            // Return a proper error message string
            const errorMessage = error.response?.data?.message || error.message || "Failed to load my livestreams.";
            return rejectWithValue(errorMessage);
        }
    }
);

const livestreamSlice = createSlice({
    name: "livestream",
    initialState: {
        livestreams: [],
        loading: false,
        error: null,
        // Pagination state
        pagination: {
            page: 0,
            size: 20,
            totalElements: 0,
            totalPages: 0,
            sort: ""
        },
        // Filter state
        filters: {
            status: null,
            searchQuery: '',
            after: null,
            before: null
        }
    },
    reducers: {
        clearLivestreams: (state) => {
            state.livestreams = [];
            state.pagination = {
                page: 0,
                size: 20,
                totalElements: 0,
                totalPages: 0,
                sort: ""
            };
        },
        setFilters: (state, action) => {
            state.filters = { ...state.filters, ...action.payload };
        },
        setPagination: (state, action) => {
            state.pagination = { ...state.pagination, ...action.payload };
        },
        resetFilters: (state) => {
            state.filters = {
                status: null,
                searchQuery: '',
                after: null,
                before: null
            };
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchLivestreams.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchLivestreams.fulfilled, (state, action) => {
                state.loading = false;
                state.livestreams = action.payload.items || [];
                state.pagination = {
                    page: action.payload.page || 0,
                    size: action.payload.size || 20,
                    totalElements: action.payload.totalElements || 0,
                    totalPages: action.payload.totalPages || 0,
                    sort: action.payload.sort || ""
                };
                state.error = null;
            })
            .addCase(fetchLivestreams.rejected, (state, action) => {
                state.loading = false;
                // Handle error object properly
                const errorPayload = action.payload;
                state.error = typeof errorPayload === 'string' 
                    ? errorPayload 
                    : errorPayload?.message || "Failed to load livestreams.";
                state.livestreams = []; // Clear livestreams on error
            })
            .addCase(fetchMyLivestreams.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchMyLivestreams.fulfilled, (state, action) => {
                state.loading = false;
                state.livestreams = action.payload.items || [];
                state.pagination = {
                    page: action.payload.page || 0,
                    size: action.payload.size || 20,
                    totalElements: action.payload.totalElements || 0,
                    totalPages: action.payload.totalPages || 0,
                    sort: action.payload.sort || ""
                };
                state.error = null;
            })
            .addCase(fetchMyLivestreams.rejected, (state, action) => {
                state.loading = false;
                // Handle error object properly
                const errorPayload = action.payload;
                state.error = typeof errorPayload === 'string' 
                    ? errorPayload 
                    : errorPayload?.message || "Failed to load my livestreams.";
                state.livestreams = []; // Clear livestreams on error
            });
    },
});

export const { clearLivestreams, setFilters, setPagination, resetFilters } = livestreamSlice.actions;

export default livestreamSlice.reducer;